import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '@pwc/pwc-deals-ui';
import { createProjectPagePath } from '../../services/urlService';
import isBlank from '../../../features/project/CreateProject/common/helpers/isBlank';
import './FilterDropdown.scss';

const FilterDropdown = ({
  options = [],
  label,
  placeholder,
  selectedId = '',
  onChange,
  setIsValid,
  cancelIcon = false,
  onEnter = undefined,
  onCancel = undefined,
  onBlur = undefined,
  minChars = 3,
}) => {
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const ref = useRef(null);
  const location = useLocation();
  const selectedOption =
    options.find((option) => +selectedId === +option.value) || {};
  const [selectedValue, setSelectedValue] = useState(
    selectedOption.label || ''
  );
  const [showCancel, setShowCancel] = useState(false);

  const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
      if (didMount.current) {
        func();
      } else {
        didMount.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
  };

  useDidMountEffect(() => {
    if (isBlank(selectedValue)) {
      onChange(null);
    } else if (selectedOption.label === selectedValue) {
      setIsValid(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  useEffect(() => {
    const prevOption =
      options.find((option) => +selectedId === +option.value) || {};
    setSelectedValue(prevOption.label || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const filter = (e) => {
    setOpen(true);
    setSelectedValue(e.target.value || '');
    if (e.target.value === '') {
      if (onCancel) {
        onCancel();
      }
      setShowCancel(false);
    }
    if (e.target.value.length > 0) {
      setShowCancel(true);
    }
    const filteredValues = options.filter((option) => {
      return (
        option.label.toLowerCase().indexOf(selectedValue.toLowerCase()) > -1
      );
    });
    setFilteredOptions(filteredValues);
  };

  const handleDomClick = (e) => {
    setOpen(e && e.target === ref.current);
  };

  useEffect(() => {
    document.addEventListener('click', handleDomClick);

    return () => document.removeEventListener('click', handleDomClick);
  }, []);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  const handleSelect = (option) => {
    setSelectedValue(option.label);
    onChange(option.value);
  };

  const handleCancel = () => {
    setSelectedValue('');
    setShowCancel(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (event) => {
    if (event.target.value.length >= minChars) {
      if (event.key.toLowerCase() === 'enter') {
        if (onEnter) {
          onEnter(event.target.value);
        }
      }
    }
  };

  return (
    <>
      {label && <h6>{label}</h6>}
      {location.pathname === createProjectPagePath &&
        touched &&
        isBlank(selectedValue) && (
          <div className="a-form-error">
            <Icon
              name="alert"
              className="fill icon-error input-error-icon"
              width={16}
            />
            <span>Please select an industry or sub-sector.</span>
          </div>
        )}
      <div className="dropdown" tabIndex={-1}>
        <div
          ref={ref}
          className="control"
          role="button"
          tabIndex={-1}
          onKeyPress={handleToggle}
          onClick={handleToggle}
        >
          <span className="filter-search-icon">
            <Icon name="search" width={18} className="outline" />
          </span>
          <input
            tabIndex={0}
            className="selected-value"
            type="text"
            placeholder={placeholder}
            value={selectedValue}
            onChange={(e) => filter(e)}
            onBlur={(e) => {
              setTouched(true);
              if (onBlur && e.target.value.length >= minChars) {
                onBlur(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
          />
          {cancelIcon && showCancel && (
            <button
              type="button"
              className="filter-cancel-icon"
              onClick={handleCancel}
            >
              <Icon name="circle-cross" width="24" className="outline" />
            </button>
          )}
        </div>
        {filteredOptions.length > 0 && (
          <div className={`options ${open ? 'open' : null}`}>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="option"
                role="button"
                tabIndex={0}
                onKeyPress={() => handleSelect(option)}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default FilterDropdown;
